/**
 * listing-index — the index page that lists every home.
 * Webflow export: listings.html · webpack entry: `listings`.
 * Map: IndexMap (a marker per home, with filters + pagination).
 *
 * The detail page of one home is `src/pages/listing/`.
 */
import { onReady } from '../../shared/utils/helpers';
import {
  BREAKPOINTS,
  COLLECTION_WRAP_SELECTOR,
  MAPBOX_TOKEN
} from '../../shared/utils/variables';
import Base from '../../components/base/base';
import CookieConsent from '../../components/cookie-consent/cookie-consent';
import Header from '../../components/header/header';
import KLIndex from '../../components/kl-index/kl-index';
import IndexPagination from '../../components/index-pagination/index-pagination';
import IndexFilters from '../../components/index-filters/index-filters';
import IndexMap from '../../components/index-map/index-map';
import IndexViewToggle from '../../components/index-view-toggle/index-view-toggle';
import './listing-index.scss';

interface ListingIndexPageOptions {
  debug?: boolean;
  [key: string]: any;
}

class ListingIndexPage extends Base {
  // private header: Header;
  private pagination: IndexPagination | null = null;
  private filters: IndexFilters | null = null;
  private map: IndexMap | null = null;
  private viewToggle: IndexViewToggle | null = null;

  constructor(options: ListingIndexPageOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);
    this.debug = options.debug || false;

    // Initialize header with same debug setting for consistent logging
    // this.header = new Header({ debug: this.debug });

    this.init();
  }

  init(): void {
    this.log('ListingIndexPage initialized');

    // Cookie consent gate. Webflow holds the Meta Pixel until this grants.
    try {
      new CookieConsent({ debug: this.debug });
      this.log('CookieConsent initialized');
    } catch (e) {
      this.log('CookieConsent init failed or skipped', e);
    }

    // Test CMS items that must not appear on the live site. They are removed
    // here — while the collection wrap still ships hidden, before any
    // component snapshots the item list — so they never render as cards,
    // never count in the filters, and never reach the map.
    const HIDDEN_ADDRESSES = ['9999 Townsend St,', '8888 Townsend St,'];
    HIDDEN_ADDRESSES.forEach((address) => {
      document
        .querySelectorAll<HTMLElement>(`.ml-item[data-full-address^="${address}"]`)
        .forEach((item) => item.remove());
    });

    new KLIndex({ debug: this.debug });

    // Initialize listings pagination (also reveals the hidden collection)
    try {
      this.pagination = new IndexPagination({
        debug: this.debug,
        pageSize: 9,
        wrapSelector: COLLECTION_WRAP_SELECTOR
      });
      this.log('IndexPagination initialized');
    } catch (e) {
      this.log('IndexPagination init failed', e);
      // Fail open: never leave the inventory hidden if pagination breaks
      document.querySelector(COLLECTION_WRAP_SELECTOR)?.classList.remove('hidden');
    }

    // Initialize the map column; filter changes refit it to the matching set
    try {
      this.map = new IndexMap({
        debug: this.debug,
        accessToken: MAPBOX_TOKEN,
        stickyUnder: '.nav',
        wrapSelector: COLLECTION_WRAP_SELECTOR
      });
      this.log('IndexMap initialized');
    } catch (e) {
      this.log('IndexMap init failed', e);
    }

    // Initialize listings filters; pagination re-windows the matching set and
    // the map refits to it
    try {
      this.filters = new IndexFilters({
        debug: this.debug,
        showCount: true,
        wrapSelector: COLLECTION_WRAP_SELECTOR,
        // No stickyUnder: the export's .toggle-header is sticky in the Webflow
        // CSS and wraps this container, so pinning it again here would nest a
        // sticky inside an already-pinned ancestor
        // The dropdowns stay in the bar at every width; flip inlineLayout
        // to 'stacked' for full-width lines instead of side by side.
        sheet: false,
        inlineLayout: 'row',
        filters: [
          {
            type: 'select',
            attribute: 'data-status',
            label: 'Status',
            allLabel: 'Statuses',
            options: ['Pre-Sale Available', 'Under Construction', 'Move-In Ready', 'Sold']
          },
          {
            type: 'select',
            attribute: 'data-neighborhood',
            label: 'Neighborhood',
            allLabel: 'Neighborhoods',
            initial: new URLSearchParams(window.location.search).get('neighborhood') || undefined
          },
          // No dropdown: the plan pages' See More links set the param, and
          // only the URL clears it
          {
            type: 'select',
            attribute: 'data-base-plan',
            label: 'Plan',
            hidden: true,
            // 'prefix': ?plan=the-kombi catches the-kombi-coupe and -grande
            match: 'prefix',
            initial: new URLSearchParams(window.location.search).get('plan') || undefined
          }
        ],
        onChange: (matching) => {
          this.pagination?.setItems(matching);
          this.map?.setItems(matching);
        }
      });
      this.log('IndexFilters initialized');
    } catch (e) {
      this.log('IndexFilters init failed', e);
    }

    // Mobile-only List/Map toggle. The map is hidden ≤767px; this swaps the
    // card list for it on tap and activates the map (creating it on first
    // show, so phones in list view never load Mapbox).
    try {
      this.viewToggle = new IndexViewToggle({
        debug: this.debug,
        containerSelector: '.map-page-container',
        navSelector: '.nav',
        onChange: (view) => {
          if (view === 'map') this.map?.activate();
        }
      });
      this.log('IndexViewToggle initialized');
    } catch (e) {
      this.log('IndexViewToggle init failed', e);
    }
  }

  /**
   * Cleanup method
   */
  destroy(): void {
    if (this.pagination) {
      this.pagination.destroy();
    }

    if (this.filters) {
      this.filters.destroy();
    }

    if (this.map) {
      this.map.destroy();
    }

    if (this.viewToggle) {
      this.viewToggle.destroy();
    }
  }
}

onReady(() => {
  // You can enable debug mode by passing { debug: true }
  new ListingIndexPage({ debug: true });
});

export default ListingIndexPage;
